<script setup lang="ts">
import { onClickOutside } from '@vueuse/core'

import type { NumericFilter, SearchFacetTuple } from '~/types/search/types'

const { t } = useI18n({ useScope: 'local' })

const { setLock } = useLockBody()
const { bathrooms, bedrooms } = useSearchNumericFilters()
const { minBudget, maxBudget } = useSearchBudgetFilters()
const { facetFilters } = useSearchFacetFilters()
const { startDate, endDate } = useSearchDateFilters()
const { facets, isLoading, nbHits } = useSearchResults({ startDate, endDate })

const modalFiltersOpen = defineModel<boolean>({ required: true })
defineEmits<{
  'change-facet': SearchFacetTuple
  'clear-filter-numeric': [NumericFilter]
  'refine-filter-numeric': [NumericFilter, number]
  'reset-filters': []
  'update-budget': [string, string]
}>()

const target = ref<HTMLDivElement | null>(null)
const filtersUsed = ref(false)

onClickOutside(target, () => {
  modalFiltersOpen.value = false
})

watch(modalFiltersOpen, (value) => {
  setLock(value)
  if (filtersUsed.value) {
    filtersUsed.value = false
    window.scrollTo(0, 0)
  }
})

watch(nbHits, () => {
  filtersUsed.value = true
})
</script>

<template>
  <div
    v-if="modalFiltersOpen"
    class="fixed inset-x-0 top-0 z-modal h-screen w-full bg-gray-700/50 backdrop-blur transition duration-300 ease-in"
  />
  <div
    ref="target"
    :class="[
      'fixed left-4 top-4 z-[1060] w-full overflow-hidden bg-white transition-all duration-300 ease-in lg:w-[341px]',
      {
        '-translate-x-full lg:translate-x-[-395px]': !modalFiltersOpen,
        'translate-x-0': modalFiltersOpen,
      },
    ]"
  >
    <div class="py-4">
      <div class="mb-6 flex justify-between px-6">
        <div class="font-bold">{{ t('allFilters') }}</div>
        <button type="button" @click="modalFiltersOpen = false">
          <BaseIcon name="close" :size="1.5" />
        </button>
      </div>
      <SearchFilterSidebar
        class="h-[calc(100vh-160px-1rem)] px-6"
        :bathrooms="bathrooms"
        :bedrooms="bedrooms"
        :facet-filters="facetFilters"
        :facets="facets"
        :loading-facets="isLoading"
        :max-budget="maxBudget"
        :min-budget="minBudget"
        @change-facet="
          (facet, facetValue) => $emit('change-facet', facet, facetValue)
        "
        @clear-filter-numeric="$emit('clear-filter-numeric', $event)"
        @refine-filter-numeric="
          (attr, value) => $emit('refine-filter-numeric', attr, value)
        "
        @update-budget="(max, min) => $emit('update-budget', max, min)"
      />
      <div class="mt-2 flex justify-between border-t border-gray-200 px-6 pt-4">
        <BaseButton
          class="flex items-center !px-3 !py-2 !text-md"
          color="secondary"
          variant="outline"
          @click="$emit('reset-filters')"
        >
          {{ t('eraseAll') }}
        </BaseButton>
        <BaseButton
          color="secondary"
          class="w-[168px] !px-3 !py-2 !text-md"
          :disabled="nbHits === 0 || isLoading"
          @click="modalFiltersOpen = false"
        >
          {{ t('seeProperties', { count: nbHits }) }}
        </BaseButton>
      </div>
    </div>
  </div>
</template>

<i18n lang="json">
{
  "en": {
    "allFilters": "All filters",
    "eraseAll": "Erase all",
    "seeProperties": "No property | See {count} propertie | See {count} properties"
  },
  "fr": {
    "allFilters": "Tous les filtres",
    "eraseAll": "Tout effacer",
    "seeProperties": "Aucune propriété | Voir {count} propriété | Voir {count} propriétés"
  }
}
</i18n>
